import { useEffect, useState } from "react";

interface RankTier {
  name: string;
  level: number;
  color: string;
  glow: string;
  icon: string;
  desc: string;
}

export function getRepTier(rep: number): RankTier {
  if (rep >= 81) return { name: "부장", level: 5, color: "#EAB308", glow: "rgba(234,179,8,0.5)", icon: "ri-vip-crown-fill", desc: "최고 경영진 수준의 역량을 인정받았습니다" };
  if (rep >= 61) return { name: "과장", level: 4, color: "#E8375F", glow: "rgba(232,55,95,0.5)", icon: "ri-medal-2-fill", desc: "팀을 이끌 수 있는 리더십을 갖추었습니다" };
  if (rep >= 41) return { name: "대리", level: 3, color: "#FF9F43", glow: "rgba(255,159,67,0.5)", icon: "ri-award-fill", desc: "현장 업무를 주도적으로 처리할 수 있습니다" };
  if (rep >= 21) return { name: "사원", level: 2, color: "#7A9BFF", glow: "rgba(74,108,247,0.5)", icon: "ri-user-star-fill", desc: "기본 업무 역량을 갖춘 정식 구성원입니다" };
  return { name: "인턴", level: 1, color: "#8B91A8", glow: "rgba(139,145,168,0.4)", icon: "ri-user-line", desc: "첫 발을 내딛은 새로운 시작입니다" };
}

interface Props {
  prevRank: string;
  newRank: string;
  reputation: number;
  onClose: () => void;
}

const PARTICLES = Array.from({ length: 18 }, (_, i) => ({
  id: i,
  left: `${5 + (i * 5.5) % 90}%`,
  delay: `${(i * 0.13) % 1.2}s`,
  size: i % 3 === 0 ? 8 : i % 3 === 1 ? 5 : 6,
  colors: ["#EAB308", "#E8375F", "#FF9F43", "#7A9BFF", "#00C896", "#A78BFA"],
}));

export default function RankUpModal({ prevRank, newRank, reputation, onClose }: Props) {
  const tier = getRepTier(reputation);
  const [visible, setVisible] = useState(false);
  const [countdown, setCountdown] = useState(6);

  useEffect(() => {
    // 스타일 인젝션
    const style = document.createElement("style");
    style.id = "rank-up-keyframes";
    style.innerHTML = `
      @keyframes rankCardAppear {
        0% { opacity: 0; transform: scale(0.6) translateY(30px); }
        60% { transform: scale(1.04) translateY(-4px); }
        100% { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes rankFloatParticle {
        0% { opacity: 0.9; transform: translateY(0) scale(1) rotate(0deg); }
        100% { opacity: 0; transform: translateY(-100px) scale(0.3) rotate(180deg); }
      }
      @keyframes rankGlowPulse {
        0%, 100% { opacity: 0.5; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.2); }
      }
      @keyframes rankBadgeSpin {
        0% { transform: rotateY(0deg); }
        50% { transform: rotateY(180deg); }
        100% { transform: rotateY(360deg); }
      }
      @keyframes rankShimmer {
        0% { background-position: -200% center; }
        100% { background-position: 200% center; }
      }
      @keyframes rankOverlayIn {
        0% { opacity: 0; }
        100% { opacity: 1; }
      }
    `;
    if (!document.getElementById("rank-up-keyframes")) {
      document.head.appendChild(style);
    }
    setTimeout(() => setVisible(true), 50);
    return () => {
      const el = document.getElementById("rank-up-keyframes");
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (!visible) return;
    const tick = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(tick); onClose(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [visible, onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ animation: "rankOverlayIn 0.3s ease forwards" }}
    >
      {/* 파티클 배경 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {PARTICLES.map((p) => (
          <div
            key={p.id}
            className="absolute rounded-full"
            style={{
              left: p.left,
              bottom: "30%",
              width: p.size,
              height: p.size,
              backgroundColor: p.colors[p.id % p.colors.length],
              animation: `rankFloatParticle ${1.2 + (p.id % 4) * 0.3}s ${p.delay} ease-out infinite`,
              opacity: 0,
            }}
          />
        ))}
      </div>

      {/* 오버레이 */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-md cursor-pointer"
        onClick={onClose}
      />

      {/* 메인 카드 */}
      <div
        className="relative w-full max-w-sm overflow-hidden"
        style={{
          animation: visible ? "rankCardAppear 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards" : "none",
          opacity: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 글로우 아우라 */}
        <div
          className="absolute -inset-8 rounded-full"
          style={{
            background: `radial-gradient(circle, ${tier.glow} 0%, transparent 70%)`,
            animation: "rankGlowPulse 2s ease-in-out infinite",
          }}
        />

        <div
          className="relative rounded-2xl overflow-hidden border"
          style={{ backgroundColor: "#0A0B0F", borderColor: `${tier.color}30` }}
        >
          {/* 상단 그라데이션 띠 */}
          <div
            className="h-1 w-full"
            style={{
              background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)`,
              animation: "rankShimmer 2s linear infinite",
              backgroundSize: "200% 100%",
            }}
          />

          <div className="px-7 py-8 text-center">
            {/* 직급 아이콘 */}
            <div className="relative inline-flex items-center justify-center mb-6">
              <div
                className="absolute inset-0 rounded-full"
                style={{
                  background: `radial-gradient(circle, ${tier.glow} 0%, transparent 65%)`,
                  animation: "rankGlowPulse 1.5s ease-in-out infinite",
                }}
              />
              <div
                className="relative w-24 h-24 rounded-full flex items-center justify-center border-2"
                style={{
                  backgroundColor: `${tier.color}18`,
                  borderColor: `${tier.color}50`,
                }}
              >
                <i
                  className={`${tier.icon} text-4xl`}
                  style={{ color: tier.color }}
                />
              </div>
            </div>

            {/* LEVEL UP 배지 */}
            <div
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4 border"
              style={{
                color: tier.color,
                borderColor: `${tier.color}40`,
                backgroundColor: `${tier.color}12`,
              }}
            >
              <i className="ri-arrow-up-double-line text-sm"></i>
              LEVEL UP
            </div>

            {/* 직급 이름 */}
            <h2
              className="text-4xl font-extrabold mb-1 tracking-tight"
              style={{ color: tier.color }}
            >
              {newRank}
            </h2>

            {/* 직급 변경 표시 */}
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-[#333333] text-sm">{prevRank}</span>
              <div className="flex items-center gap-1">
                <div className="w-8 h-px bg-[#2A2A2A]" />
                <i className="ri-arrow-right-s-line text-[#555555] text-sm"></i>
                <div className="w-8 h-px bg-[#2A2A2A]" />
              </div>
              <span className="font-bold text-sm" style={{ color: tier.color }}>{newRank}</span>
            </div>

            <p className="text-[#555555] text-xs leading-relaxed mb-6">{tier.desc}</p>

            {/* 직급 점수 */}
            <div
              className="rounded-xl border px-4 py-3 mb-5"
              style={{ borderColor: `${tier.color}20`, backgroundColor: `${tier.color}08` }}
            >
              <div className="text-[#444444] text-[10px] uppercase tracking-widest mb-2">현재 직급 점수</div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl font-bold tabular-nums" style={{ color: tier.color }}>
                  {reputation}
                </span>
                <span className="text-[#444444] text-sm">/ 100 pt</span>
              </div>
              {/* 프로그레스 바 */}
              <div className="w-full h-1.5 bg-[#1A1A1A] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-1000"
                  style={{
                    width: `${reputation}%`,
                    background: `linear-gradient(90deg, ${tier.color}80, ${tier.color})`,
                  }}
                />
              </div>
              {reputation < 100 && (
                <div className="text-[#333333] text-[10px] mt-1.5 text-right">
                  다음 직급까지 {(Math.ceil(reputation / 20) * 20) - reputation}pt 남음
                </div>
              )}
            </div>

            {/* 닫기 버튼 */}
            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              style={{
                backgroundColor: `${tier.color}20`,
                color: tier.color,
                border: `1px solid ${tier.color}30`,
              }}
            >
              확인 ({countdown}초 후 자동 닫힘)
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
